import { VIOLATION_REVIEW_NOTE_MAX_LENGTH } from '@aysar/contracts';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { ServerSelect } from '@/shared/components/selection/ServerSelect/ServerSelect';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { useViolationTypeIdOptions } from '../../hooks/useViolationTypeOptions/useViolationTypeOptions';
import { DecisionRefusal } from '../DecisionRefusal/DecisionRefusal';
import { useEnforceViolationModalLogic } from '../EnforceViolationModal/useEnforceViolationModalLogic';
import { ViolationConsequence } from '../ViolationConsequence/ViolationConsequence';

/**
 * Name the rule the classifier could not, then confirm.
 *
 * The row reached the queue with **no rule at all** — an AI refusal, or a
 * provider outage — so `POST /enforce` requires a `violationTypeId` here and
 * answers 422 without one. The Confirm button is therefore disabled until a rule
 * is chosen: a refusal the operator could have been spared is a refusal that
 * should never have been reachable.
 *
 * The consequence appears **after** the choice and comes from the chosen rule,
 * not from this dialog. That ordering is the point: the moderator is supplying a
 * classification, and the consequence follows from it — which is why there is no
 * action picker here either (N20).
 */
export function ClassifyAndEnforceModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('violations');
  const {
    violation,
    isLoading,
    reload,
    violationTypeId,
    setChosenTypeId,
    note,
    setNote,
    canSubmit,
    isSaving,
    submit,
    error,
    isConflict,
    isGone,
  } = useEnforceViolationModalLogic(modalData, onCancel, { isClassifying: true });

  const rereadAndClose = () => {
    reload();
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('classify.title')}</DialogTitle>
          <DialogDescription>{t('classify.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <div className="flex flex-col gap-md" aria-label={t('review.loading')}>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : violation === null ? null : (
            <div className="flex flex-col gap-lg">
              <div className="flex flex-col gap-xs">
                <Label htmlFor="violation-classify-type">{t('classify.pick')}</Label>
                <ServerSelect
                  id="violation-classify-type"
                  aria-describedby="violation-classify-type-hint"
                  value={violationTypeId}
                  onValueChange={setChosenTypeId}
                  // Server-searched, never a preloaded list with a huge page
                  // size (W6) — the catalogue is operator-authored and grows.
                  useOptionsQuery={useViolationTypeIdOptions}
                  placeholder={t('classify.pickPlaceholder')}
                  searchPlaceholder={t('classify.pickSearch')}
                  emptyLabel={t('classify.pickEmpty')}
                />
                <p id="violation-classify-type-hint" className="text-caption text-muted-foreground">
                  {t('classify.pickHint')}
                </p>
              </div>

              <ViolationConsequence
                violationTypeId={violationTypeId}
                label={t('classify.thenWillDo')}
              />

              <p className="flex items-start gap-sm text-body text-muted-foreground">
                <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('enforce.noOverride')}</span>
              </p>

              <div className="flex flex-col gap-xs">
                <Label htmlFor="violation-classify-note">{t('enforce.note')}</Label>
                <Textarea
                  id="violation-classify-note"
                  aria-describedby="violation-classify-note-hint"
                  value={note}
                  maxLength={VIOLATION_REVIEW_NOTE_MAX_LENGTH}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
                <p id="violation-classify-note-hint" className="text-caption text-muted-foreground">
                  {t('enforce.noteHint')}
                </p>
              </div>

              <DecisionRefusal
                message={error}
                isConflict={isConflict}
                isGone={isGone}
                onReload={rereadAndClose}
                onClose={onCancel}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('enforce.cancel')}
          </Button>
          <Button
            isLoading={isSaving}
            disabled={!canSubmit}
            onClick={() => {
              void submit();
            }}
          >
            {t('enforce.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
